// ─── Intégration ComIT (Telipso Tâches) ─────────────────────────
// Le RMM ne crée pas le bon de travail lui-même : il ouvre l'écran ComIT
// `/rmm/bon` avec les descriptions courte/longue et le client déjà résolus en
// paramètres d'URL. Le technicien s'authentifie côté ComIT (cookie partagé
// avec l'écran 3CX) puis valide/ajuste avant création.

import { useState, useEffect } from 'react';
import { TicketPlus, Ticket, X } from 'lucide-react';
import { api } from './api.jsx';
import { cveUrl } from './format.jsx';

// La config ne change pas pendant une session : une seule requête, partagée
// par tous les composants qui la demandent (AlertsTable en instancie une par
// page, la fiche client une de plus).
let configPromise = null;

export function loadComitConfig() {
  if (!configPromise) {
    configPromise = api
      .get('/platform/comit/config')
      .then((r) => r || { enabled: false, canSearch: false, baseUrl: '' })
      .catch(() => ({ enabled: false, canSearch: false, baseUrl: '' }));
  }
  return configPromise;
}

/** { enabled, canSearch, baseUrl } — `enabled` false tant que la config n'est pas chargée. */
export function useComitConfig() {
  const [config, setConfig] = useState({ enabled: false, canSearch: false, baseUrl: '' });
  useEffect(() => {
    let active = true;
    loadComitConfig().then((c) => {
      if (active) setConfig(c);
    });
    return () => {
      active = false;
    };
  }, []);
  return config;
}

const MAX_DESCRIPTION = 100; // `bontravail.description` est en varchar(100)

/**
 * Descriptions courte et longue d'un bon de travail issu d'une alerte.
 * Les libellés catégorie/sévérité sont passés par l'appelant : ils vivent dans
 * AlertsTable, qui importe ce module (les garder ici créerait un cycle).
 */
export function buildAlertTaskText(alert, { catLabel = '', sevLabel = '' } = {}) {
  const appareil = alert.computerName || 'Appareil inconnu';
  const courte = `RMM — ${appareil} : ${alert.title || catLabel || 'Alerte'}`;

  const valeur =
    alert.currentValue != null
      ? alert.currentValue.toFixed(1) +
        '%' +
        (alert.thresholdValue != null ? ` (seuil ${alert.thresholdValue.toFixed(1)}%)` : '')
      : '—';

  const lignes = [
    'Bon créé depuis une alerte Fonxtio RMM.',
    '',
    `Appareil     : ${appareil}`,
    `Client       : ${alert.clientName || '—'}`,
    `Site         : ${alert.siteName || '—'}`,
    `Catégorie    : ${catLabel || '—'}`,
    `Sévérité     : ${sevLabel || '—'}`,
    `Valeur       : ${valeur}`,
    `Détectée le  : ${alert.occurredAt ? new Date(alert.occurredAt).toLocaleString('fr-CA') : '—'}`,
    '',
    alert.title || '',
    alert.message || '',
  ];

  return { description: courte.slice(0, MAX_DESCRIPTION), descriptiontravail: lignes.join('\n').trim() };
}

/**
 * Descriptions pour un bon issu d'une vulnérabilité logicielle (page
 * Vulnérabilités). Distinct de `buildAlertTaskText` : le travail attendu est
 * « mettre à jour ce logiciel », pas « regarder cette alerte ».
 */
export function buildCveTaskText(vuln) {
  const appareil = vuln.computerName || 'Appareil inconnu';
  const logiciel = [vuln.softwareName, vuln.softwareVersion].filter(Boolean).join(' ');
  // `cveIds` (optionnel) : un logiciel peut porter plusieurs CVE — un seul bon
  // « mettre à jour ce logiciel » vaut mieux qu'un bon par CVE.
  const cves = Array.isArray(vuln.cveIds) && vuln.cveIds.length ? vuln.cveIds : [vuln.cveId].filter(Boolean);
  const refCourte = cves.length > 1 ? `${cves.length} CVE` : cves[0] || 'CVE';
  const courte = `RMM — ${appareil} : mise à jour ${logiciel || 'logiciel'} (${refCourte})`;

  const lignes = [
    'Bon créé depuis une vulnérabilité détectée par Fonxtio RMM.',
    '',
    `Appareil     : ${appareil}`,
    `Client       : ${vuln.clientName || '—'}`,
    `Logiciel     : ${logiciel || '—'}`,
    `CVE          : ${cves.length ? cves.join(', ') : '—'}`,
    `Sévérité     : ${vuln.cvssSeverity || '—'}${vuln.cvssScore != null ? ` (CVSS ${vuln.cvssScore.toFixed(1)})` : ''}`,
    `Détectée le  : ${vuln.detectedAt ? new Date(vuln.detectedAt).toLocaleString('fr-CA') : '—'}`,
    cves.length === 1 ? `Détail       : ${cveUrl(cves[0])}` : '',
    '',
    vuln.cveDescription || '',
  ];

  return { description: courte.slice(0, MAX_DESCRIPTION), descriptiontravail: lignes.join('\n').trim() };
}

/**
 * URL de l'écran ComIT « créer un bon », descriptions pré-remplies.
 * `reference` est le jeton renvoyé par `/platform/comit/bon/ref` : ComIT le
 * recopie tel quel dans son rappel, c'est ce qui permet d'estampiller les
 * bonnes lignes avec le numéro attribué (cf. createBonRef).
 */
export function buildComitBonUrl(
  baseUrl,
  { description, descriptiontravail, comitClientId, appareil, reference }
) {
  const p = new URLSearchParams();
  p.set('description', description || '');
  p.set('descriptiontravail', descriptiontravail || '');
  if (comitClientId > 0) p.set('client', String(comitClientId));
  if (appareil) p.set('appareil', appareil);
  if (reference) p.set('rmmref', reference);
  return `${(baseUrl || '').replace(/\/+$/, '')}/rmm/bon?${p.toString()}`;
}

// ─── Liaison ligne RMM ↔ bon de travail ─────────────────────────
// Le numéro de bon n'existe qu'après la création côté ComIT. Le RMM enregistre
// donc AVANT d'ouvrir l'onglet ce que le futur bon prendra en charge, sous un
// jeton opaque ; ComIT rappelle le serveur avec ce jeton et le numéro.
//
// `cible` = { alertIds: [...], vulnerabilityIds: [...] }. Les vulnérabilités
// sont étendues côté serveur à tout le logiciel de l'appareil : un bon « mettre
// à jour AnyDesk » couvre ses dix CVE, pas la seule ligne cliquée.

/** Jeton de liaison, ou '' si l'appel échoue (permission, réseau). */
export async function createBonRef(cible) {
  if (!cible || (!cible.alertIds?.length && !cible.vulnerabilityIds?.length)) return '';
  const r = await api.post('/platform/comit/bon/ref', {
    alertIds: cible.alertIds || [],
    vulnerabilityIds: cible.vulnerabilityIds || [],
  });
  return r?.reference || '';
}

/**
 * Assignation manuelle à un bon existant. `numero = 0` délie.
 * Renvoie { ok, alerts, vulnerabilities, message }.
 */
export async function assignBon(numero, cible) {
  const r = await api.fetch('/platform/comit/bon/assign', {
    method: 'POST',
    body: JSON.stringify({
      bonNumero: Number(numero) || 0,
      alertIds: cible?.alertIds || [],
      vulnerabilityIds: cible?.vulnerabilityIds || [],
    }),
  });
  let corps = null;
  try {
    corps = await r.json();
  } catch {
    corps = null;
  }
  if (!r.ok) {
    return {
      ok: false,
      message:
        corps?.message ||
        (r.status === 403
          ? 'Permission manquante pour assigner un bon à ces lignes'
          : 'Assignation refusée (' + r.status + ')'),
    };
  }
  return { ok: true, alerts: corps?.alerts || 0, vulnerabilities: corps?.vulnerabilities || 0 };
}

/**
 * Bouton « Tâche ComIT » partagé par tous les endroits qui listent des alertes
 * ou des CVE. Ne rend rien si l'intégration n'est pas configurée, pour que les
 * appelants n'aient pas à tester `comit.enabled` eux-mêmes.
 *
 * `texte` : { description, descriptiontravail } (cf. buildAlertTaskText /
 * buildCveTaskText). Onglet séparé : le technicien finit la saisie côté ComIT
 * sans perdre sa place dans la liste.
 *
 * `cible` : { alertIds, vulnerabilityIds } — les lignes que le bon prendra en
 * charge. Fournie, le numéro attribué revient tout seul sur ces lignes (rappel
 * de ComIT) ; absente, le bouton se comporte comme avant (aucune liaison).
 */
export function ComitTaskButton({
  texte,
  comitClientId = 0,
  appareil = '',
  cible = null,
  compact = false,
  // Barre d'actions d'un tableau : l'icône seule, le sens passant par
  // l'infobulle. Les écrans qui ont la place gardent le libellé.
  iconOnly = false,
  className = '',
}) {
  const comit = useComitConfig();
  if (!comit.enabled) return null;

  const ouvrir = async (e) => {
    e.stopPropagation();
    // L'onglet est ouvert AVANT l'await : après, le navigateur ne rattache plus
    // window.open au clic de l'utilisateur et le bloque comme une pop-up. On
    // ouvre donc vide, puis on navigue quand le jeton est là.
    const onglet = cible ? window.open('', '_blank') : null;
    let reference = '';
    if (cible) {
      try {
        reference = await createBonRef(cible);
      } catch {
        // Jeton indisponible (permission, réseau) : le bon reste créable, il ne
        // sera simplement pas rattaché automatiquement.
        reference = '';
      }
    }
    const url = buildComitBonUrl(comit.baseUrl, { ...texte, comitClientId, appareil, reference });
    if (onglet) onglet.location = url;
    else window.open(url, '_blank', 'noopener,noreferrer');
  };

  const taille = iconOnly
    ? 'p-1'
    : compact
      ? 'text-[10px] px-2 py-0.5 gap-0.5'
      : 'text-[11px] px-2.5 py-0.5 gap-1';

  return (
    <button
      type="button"
      onClick={ouvrir}
      title={
        comitClientId
          ? 'Créer un bon de travail ComIT'
          : "Créer un bon de travail ComIT — ce client RMM n'est pas lié à un client ComIT, il faudra le choisir"
      }
      className={`border border-blue-500/40 rounded-md text-blue-400 cursor-pointer bg-blue-500/10 inline-flex items-center whitespace-nowrap hover:bg-blue-500/20 ${taille} ${className}`}
    >
      <TicketPlus size={iconOnly ? 13 : compact ? 10 : 11} strokeWidth={2} />
      {iconOnly ? null : compact ? 'ComIT' : 'Tache ComIT'}
    </button>
  );
}

/**
 * Pastille « Bon 12345 » sur une ligne déjà prise en charge. Remplace le bouton
 * de création : c'est ce qui évite qu'un second technicien ouvre un deuxième bon
 * pour le même incident.
 *
 * Pas de lien vers ComIT : seul le numéro de bon est stocké (l'assignation
 * manuelle ne connaît que lui), et l'écran ComIT du bon s'ouvre sur son
 * `idbontravail`, pas sur son numéro.
 */
export function ComitBonChip({ numero, linkedAt, linkedBy, onDetach = null, compact = false }) {
  if (!numero) return null;

  const auteur = linkedBy === 'system:comit-callback' ? 'ComIT' : linkedBy || '';
  const quand = linkedAt ? new Date(linkedAt).toLocaleString('fr-FR') : '';
  const taille = compact ? 'text-[10px] px-2 py-0.5 gap-0.5' : 'text-[11px] px-2.5 py-0.5 gap-1';

  return (
    <span
      title={`Bon de travail ComIT nº ${numero}${auteur ? ' — assigné par ' + auteur : ''}${
        quand ? ' le ' + quand : ''
      }`}
      className={`border border-emerald-500/40 rounded-md text-emerald-300 bg-emerald-500/10 inline-flex items-center whitespace-nowrap ${taille}`}
    >
      <Ticket size={compact ? 10 : 11} strokeWidth={2} />
      Bon {numero}
      {onDetach && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDetach();
          }}
          title="Retirer la liaison au bon"
          className="bg-transparent border-0 p-0 ml-0.5 text-emerald-300/70 cursor-pointer hover:text-emerald-200 inline-flex"
        >
          <X size={compact ? 9 : 10} strokeWidth={2.5} />
        </button>
      )}
    </span>
  );
}

/**
 * Boîte de saisie manuelle : rattache la sélection courante à un bon déjà créé.
 * Le pendant du chemin automatique, pour les bons ouverts par téléphone, par
 * courriel, ou avant que le RMM ne signale l'incident.
 */
export function ComitBonAssignModal({ cible, resume = '', onClose, onDone }) {
  const [numero, setNumero] = useState('');
  const [busy, setBusy] = useState(false);
  const [erreur, setErreur] = useState('');

  const total = (cible?.alertIds?.length || 0) + (cible?.vulnerabilityIds?.length || 0);

  const valider = async (e) => {
    e.preventDefault();
    const n = parseInt(numero, 10);
    if (!(n > 0)) {
      setErreur('Numéro de bon invalide');
      return;
    }
    setBusy(true);
    setErreur('');
    const r = await assignBon(n, cible);
    setBusy(false);
    if (!r.ok) {
      setErreur(r.message);
      return;
    }
    onDone && onDone(r, n);
    onClose && onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={valider}
        className="bg-card border border-rule rounded-2xl p-5 w-[380px] max-w-full"
      >
        <div className="text-fg font-bold text-sm mb-1 flex items-center gap-2">
          <Ticket size={15} strokeWidth={1.75} className="text-emerald-300" />
          Assigner à un bon de travail
        </div>
        <div className="text-[11px] text-muted-2 mb-3">
          {resume || `${total} ligne${total > 1 ? 's' : ''} sélectionnée${total > 1 ? 's' : ''}`}
        </div>

        <label className="text-[11px] text-muted block mb-1">Numéro du bon ComIT</label>
        <input
          autoFocus
          inputMode="numeric"
          value={numero}
          onChange={(e) => setNumero(e.target.value.replace(/[^0-9]/g, ''))}
          placeholder="12345"
          className="w-full bg-sidebar border border-rule rounded-lg px-3 py-1.5 text-fg-2 text-sm outline-none font-mono focus:border-blue-500"
        />
        <div className="text-[10px] text-muted-2 mt-1.5">
          Les vulnérabilités sont étendues à tout le logiciel de l'appareil : un bon de mise à jour couvre
          toutes ses CVE.
        </div>

        {erreur && <div className="text-crit text-[11px] mt-2">{erreur}</div>}

        <div className="flex gap-2 justify-end mt-4">
          <button
            type="button"
            onClick={onClose}
            className="border border-rule-3 rounded-md text-muted bg-transparent text-[11px] px-3 py-1 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={busy}
            className="border border-emerald-500/40 rounded-md text-emerald-300 bg-emerald-500/10 text-[11px] px-3 py-1 cursor-pointer disabled:opacity-50"
          >
            {busy ? 'Assignation…' : 'Assigner'}
          </button>
        </div>
      </form>
    </div>
  );
}
